import { Path } from "react-router"

/**
 *  This small module allows for passing around a return URL between our
 *  components inside the search query part of an another URL. We mainly
 *  use it to facilitate for login interruptions (when a user wants to access
 *  a page but needs to login first). However, it can be used in other processes
 *  to come back to a specific page.
 *
 *  The idea is rather simple: gather parts that are important for a specific
 *  location (pathname, query, hash) from a passed URL and then serialize it
 *  into a query string that can be attached to a different URL. Then, after
 *  completing a specific process, unpack the serialized URL and proceed
 *  with the URL or pass a react-router Path to router navigation.
 *
 *  The main limitation of this mechanism is the requirement that the return
 *  URL points to our application. It's not possible to create a return URL
 *  to an arbitrary URL.
 */

/**
 *  Pack a return URL into search parameters that can be assigned inside
 *  another URL.
 */
export const packReturnURL = (location: Location | URL) => {
  const path = packReturnURLIntoPath(location)
  const query = new URLSearchParams()

  if (path.pathname.length) {
    query.set("from", path.pathname)
  }

  if (path.search.length) {
    query.set("query", path.search)
  }

  if (path.hash.length) {
    query.set("hash", path.hash)
  }

  return query
}

/**
 *  Unpack a return url from search params.
 */
export const unpackReturnURL = (search: URLSearchParams): URL | undefined => {
  const fromPart = search.get("from")
  const searchPart = search.get("query")
  const hashPart = search.get("hash")

  if (!search.has("from") && !search.has("query") && !search.has("hash")) {
    return undefined
  }

  // As a base we take the origin part of the current location. The origin
  // part is the protocol, hostname, and port. The rest of the url will be
  // filled in from the search parameters we pass from the parameter.
  const result = new URL(window.location.origin)

  if (fromPart) {
    result.pathname = fromPart
  }

  if (searchPart) {
    result.search = searchPart
  }

  if (hashPart) {
    result.hash = hashPart
  }

  return result
}

/**
 *  Unpack search params into a Path object. This will only work if the search
 *  params were previously packed with `packReturnURLIntoPath` or `packReturnURL`
 *  functions.
 */
export const unpackReturnURLIntoPath = (search: URLSearchParams): Path | undefined => {
  const url = unpackReturnURL(search)

  if (!url) {
    return undefined
  }

  // the blank initial object
  const result: Path = {
    pathname: "",
    search: "",
    hash: "",
  }

  if (url.pathname.length) {
    result.pathname = url.pathname
  }

  if (url.search.length) {
    result.search = url.search.toString()
  }

  if (url.hash.length) {
    result.hash = url.hash.toString()
  }

  return result
}

/**
 *  Pack a location into a Path object that is compatible with react-router.
 */
export const packReturnURLIntoPath = (location: Location | URL) => {
  const result: Path = {
    pathname: "",
    search: "",
    hash: "",
  }

  if (location.pathname.length) {
    result.pathname = location.pathname
  }

  if (location.search.length) {
    result.search = location.search.toString()
  }

  if (location.hash.length) {
    result.hash = location.hash.toString()
  }

  return result
}
